package or.garden.manager.features.admin

import androidx.compose.runtime.*
import karika.distribucija.ba.domain.model.ResultState
import or.garden.manager.components.ConfirmDeleteModal
import or.garden.manager.components.SnackbarType
import or.garden.manager.domain.CropService
import or.garden.manager.domain.model.CropData
import or.garden.manager.ui.utils.getCropImageSource
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.dom.*

@Composable
fun AdminCropsScreen(
    onBack: () -> Unit,
    onEditCrop: (Int?) -> Unit,
    showSnackbar: (String, SnackbarType) -> Unit = { _, _ -> }
) {
    var crops by remember { mutableStateOf<List<CropData>>(emptyList()) }
    var isLoading by remember { mutableStateOf(false) }
    var searchText by remember { mutableStateOf("") }
    var cropToDelete by remember { mutableStateOf<CropData?>(null) }

    fun loadCrops(query: String = "") {
        isLoading = true
        CropService.getCrops(query) { state ->
            when (state) {
                is ResultState.Success -> {
                    crops = (state.data.data ?: emptyList()).sortedBy { it.name }
                    isLoading = false
                }
                is ResultState.Error -> {
                    showSnackbar("Greška: ${state.message}", SnackbarType.ERROR)
                    isLoading = false
                }
                else -> {}
            }
        }
    }

    fun deleteCrop(cropId: Int) {
        // TODO: Implement CropService.deleteCrop(cropId)
        showSnackbar("Brisanje trenutno nije implementirano.", SnackbarType.INFO)
        cropToDelete = null // Close modal after action
    }

    LaunchedEffect(Unit) {
        loadCrops()
    }

    Div({ classes("max-w-6xl", "mx-auto", "pb-20") }) {
        // Header
        Div({ classes("flex", "items-center", "justify-between", "mb-8") }) {
            Div({ classes("flex", "items-center", "gap-4") }) {
                Button({
                    classes("p-2", "text-gray-500", "hover:text-emerald-600", "transition-colors")
                    onClick { onBack() }
                }) {
                    I({ classes("fas", "fa-arrow-left", "text-xl") })
                }
                H1({ classes("text-2xl", "font-bold", "text-gray-900") }) {
                    Text("Upravljanje kulturama")
                }
            }

            Button({
                classes("bg-emerald-600", "text-white", "px-6", "py-2", "rounded-xl", "hover:bg-emerald-700", "transition-colors", "font-bold", "flex", "items-center", "gap-2")
                onClick { onEditCrop(null) }
            }) {
                I({ classes("fas", "fa-plus") })
                Text("Dodaj novu kulturu")
            }
        }

        // Search
        Div({ classes("mb-8") }) {
            Div({ classes("relative") }) {
                I({ classes("fas", "fa-search", "absolute", "left-4", "top-1/2", "-translate-y-1/2", "text-gray-400") })
                Input(InputType.Text) {
                    classes("w-full", "pl-12", "pr-4", "py-3", "rounded-xl", "border", "border-gray-200", "focus:ring-2", "focus:ring-emerald-500", "focus:border-transparent", "outline-none", "transition-all")
                    placeholder("Pretraži kulture...")
                    value(searchText)
                    onInput {
                        searchText = it.value
                        loadCrops(it.value)
                    }
                }
            }
        }

        if (isLoading) {
            Div({ classes("flex", "justify-center", "py-20") }) {
                I({ classes("fas", "fa-spinner", "fa-spin", "text-4xl", "text-emerald-500") })
            }
        } else if (crops.isEmpty()) {
            Div({ classes("text-center", "py-20", "text-gray-500") }) {
                I({ classes("fas", "fa-leaf", "text-4xl", "mb-4", "opacity-20") })
                P { Text("Nema pronađenih kultura.") }
            }
        } else {
            Div({ classes("grid", "grid-cols-1", "md:grid-cols-2", "lg:grid-cols-3", "gap-6") }) {
                crops.forEach { crop ->
                    AdminCropCard(
                        crop = crop,
                        onEdit = { onEditCrop(crop.id) },
                        onDelete = { cropToDelete = crop }
                    )
                }
            }
        }

        // Delete Confirmation Modal
        cropToDelete?.let { crop ->
            ConfirmDeleteModal(
                title = "Potvrda brisanja",
                message = "Da li ste sigurni da želite obrisati kulturu '${crop.name}'?",
                onConfirm = { deleteCrop(crop.id) },
                onCancel = { cropToDelete = null }
            )
        }
    }
}

@Composable
private fun AdminCropCard(
    crop: CropData,
    onEdit: () -> Unit,
    onDelete: () -> Unit
) {
    Div({
        classes("bg-white", "rounded-2xl", "border", "border-gray-100", "p-6", "shadow-sm", "hover:shadow-md", "transition-all", "group")
    }) {
        Div({ classes("flex", "items-center", "justify-between") }) {
            Div({ classes("flex", "items-center", "gap-4") }) {
                Div({ classes("w-12", "h-12", "rounded-xl", "bg-emerald-50", "flex", "items-center", "justify-center", "overflow-hidden") }) {
                    val imageUrl = getCropImageSource(crop.icon)

                    if (imageUrl != null) {
                        Img(
                            src = imageUrl,
                            attrs = {
                                classes("w-8", "h-8", "object-contain")
                                attr("onerror", "this.src='https://ui-avatars.com/api/?name=${crop.name}&background=ecfdf5&color=059669'")
                            }
                        )
                    } else {
                        Img(
                            src = "https://ui-avatars.com/api/?name=${crop.name}&background=ecfdf5&color=059669",
                            attrs = {
                                classes("w-8", "h-8", "object-contain")
                            }
                        )
                    }
                }
                Div {
                    H3({ classes("font-bold", "text-gray-900") }) { Text(crop.name) }
                    P({ classes("text-sm", "text-gray-500", "italic") }) { Text(crop.latinName ?: "") }
                }
            }

            Div({ classes("flex", "gap-2") }) {
                Button({
                    classes("p-2", "text-gray-400", "hover:text-emerald-600", "transition-colors")
                    onClick { onEdit() }
                }) {
                    I({ classes("fas", "fa-edit") })
                }
                Button({
                    classes("p-2", "text-gray-400", "hover:text-red-600", "transition-colors")
                    onClick { onDelete() }
                }) {
                    I({ classes("fas", "fa-trash") })
                }
            }
        }
    }
}
